import { Button, Form, FormField, KInput, Spinner, View, type StackProps } from '@kit/ui'
import { Eye, EyeClosed, User, Lock } from '@tamagui/lucide-icons'
import { useForm, useStore } from '@tanstack/react-form'
import { useState } from 'react'
import { z } from 'zod'
import { useAppTheme } from '@web/app/theme/AppThemeProvider'

type SignInFormProps = {
  defaultValues?: {
    username?: string
    password?: string
  }
  submitText?: string
  onSubmit: (data: { username: string; password: string }) => Promise<void>
  onForgotPassword?: () => void
} & Omit<StackProps, 'children'>

const SignInSchema = z.object({
  username: z.string().min(1, 'Usuario requerido'),
  password: z.string().min(1, 'Contraseña requerida'),
})

export function SignInForm({
  defaultValues,
  submitText = 'Ingresar',
  onSubmit,
  onForgotPassword,
  ...props
}: SignInFormProps) {
  const [securePassword, setSecurePassword] = useState(true)
  const form = useForm({
    defaultValues: {
      username: defaultValues?.username ?? '',
      password: defaultValues?.password ?? '',
    } as z.infer<typeof SignInSchema>,
    onSubmit: async ({ value }) => {
      await onSubmit(value)
    },
  })

  const { canSubmit, isSubmitting } = useStore(form.store, ({ canSubmit, isSubmitting }) => ({
    canSubmit,
    isSubmitting,
  }))

  const { theme } = useAppTheme()
  const isDarkMode = theme.startsWith('dark')

  const handleSubmit = () => {
    if (canSubmit && !isSubmitting) {
      form.handleSubmit()
    }
  }

  const handleKeyDown = (e: any) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSubmit()
    }
  }

  return (
    <Form onSubmit={handleSubmit} autoComplete={"off" as any} gap="$true" {...props}>
      <form.Field
        name="username"
        children={(field) => (
          <FormField errors={field.state.meta.errors}>
            <View pos="relative" jc="center">
              <View pos="absolute" l={16} zi={10} pointerEvents="none">
                <User size={18} color="rgba(148, 163, 184, 1)" />
              </View>
              <KInput
                placeholder="Usuario"
                autoCapitalize="none"
                autoComplete={"off" as any}
                autoCorrect={"off" as any}
                spellCheck={false}
                value={field.state.value}
                onChangeText={field.handleChange}
                onBlur={field.handleBlur}
                onSubmitEditing={handleSubmit}
                onKeyDown={handleKeyDown}
                pl={48}
                bg="rgba(255, 255, 255, 1)"
                boc="rgba(226, 232, 240, 0.8)"
                bw={1}
                borderRadius={8}
                placeholderTextColor="rgba(148, 163, 184, 1)"
                color="rgba(15, 23, 42, 1)"
                focusStyle={{
                  boc: 'rgba(59, 130, 246, 0.5)',
                  outlineWidth: 0,
                }}
              />
            </View>
          </FormField>
        )}
      />
      <form.Field
        name="password"
        children={(field) => (
          <FormField errors={field.state.meta.errors}>
            <View pos="relative" jc="center">
              <View pos="absolute" l={16} zi={10} pointerEvents="none">
                <Lock size={18} color="rgba(148, 163, 184, 1)" />
              </View>
              <KInput
                placeholder="Contraseña"
                autoCapitalize="none"
                autoComplete={"new-password" as any}
                autoCorrect={"off" as any}
                spellCheck={false}
                value={field.state.value}
                onChangeText={field.handleChange}
                onBlur={field.handleBlur}
                secureTextEntry={securePassword}
                onSubmitEditing={handleSubmit}
                onKeyDown={handleKeyDown}
                pl={48}
                pr={48}
                bg="rgba(255, 255, 255, 1)"
                boc="rgba(226, 232, 240, 0.8)"
                bw={1}
                borderRadius={8}
                placeholderTextColor="rgba(148, 163, 184, 1)"
                color="rgba(15, 23, 42, 1)"
                focusStyle={{
                  boc: 'rgba(59, 130, 246, 0.5)',
                  outlineWidth: 0,
                }}
              />
              <View pos="absolute" r={4} zi={10}>
                <Button
                  chromeless
                  onPress={() => setSecurePassword(!securePassword)}
                  scale={0.8}
                  bg="rgba(255, 255, 255, 0)"
                  hoverStyle={{
                    bg: 'rgba(241, 245, 249, 0.6)',
                    borderColor: 'rgba(255, 255, 255, 0)',
                  }}
                  pressStyle={{
                    bg: 'rgba(226, 232, 240, 0.8)',
                    borderColor: 'rgba(255, 255, 255, 0)',
                  }}
                  focusStyle={{
                    bg: 'rgba(255, 255, 255, 0)',
                    outlineWidth: 0,
                  }}
                >
                  <Button.Icon scaleIcon={1.2}>
                    {(!securePassword && <Eye color="rgba(100, 116, 139, 1)" />) || (
                      <EyeClosed color="rgba(100, 116, 139, 1)" />
                    )}
                  </Button.Icon>
                </Button>
              </View>
            </View>
          </FormField>
        )}
      />
      {/* Olvidé mi contraseña — alineado a la derecha, sutil */}
      <View ai="flex-end" mt="$-2">
        <Button
          chromeless
          size="$2"
          bg="transparent"
          pressStyle={{ bg: 'transparent', o: 0.6 }}
          hoverStyle={{ bg: 'transparent', o: 0.8 }}
          onPress={onForgotPassword}
        >
          <Button.Text
            fontSize={13}
            color="$blue10"
            textDecorationLine="none"
            hoverStyle={{
              textDecorationLine: 'underline',
              textDecorationColor: '$blue10',
            }}
          >
            ¿Olvidaste tu contraseña?
          </Button.Text>
        </Button>
      </View>

      <View ai="center" mt="$2">
        <Form.Trigger asChild>
          <Button
            w="100%"
            h={44}
            borderRadius={8}
            bg={canSubmit ? 'rgba(26, 51, 126, 1)' : 'rgba(26, 51, 126, 0.4)'}
            disabled={!canSubmit || isSubmitting}
            disabledStyle={{
              bg: 'rgba(26, 51, 126, 0.4)',
              opacity: 0.7,
            }}
            hoverStyle={{
              bg: 'rgba(32, 63, 153, 1)',
              scale: 1.005,
            }}
            pressStyle={{
              bg: 'rgba(20, 40, 100, 1)',
              scale: 0.99,
            }}
            focusStyle={{
              outlineWidth: 0,
            }}
            onPress={handleSubmit}
          >
            {isSubmitting && (
              <Button.Icon>
                <Spinner color="rgba(255, 255, 255, 1)" />
              </Button.Icon>
            )}
            <Button.Text
              fontSize={15}
              fontWeight="600"
              color="rgba(255, 255, 255, 1)"
              letterSpacing={0.3}
            >
              {submitText}
            </Button.Text>
          </Button>
        </Form.Trigger>
      </View>
    </Form>
  )
}
